import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  Box,
  Typography,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import api from '../services/api'

const SubmitPage = () => {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [validationResults, setValidationResults] = useState(null)
  const [uploadResults, setUploadResults] = useState(null)
  const [error, setError] = useState(null)

  const validTypes = ['Dome', 'Bullet', 'PTZ', 'Box', 'Fisheye', 'Thermal', 'Multi-sensor']

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0]
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile)
      setValidationResults(null)
      setUploadResults(null)
      setError(null)
    } else {
      setError('Please select a valid CSV file')
      setFile(null)
    }
  }

  const validateCSV = (csvData) => {
    const lines = csvData.trim().split('\n')
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())

    const requiredHeaders = ['manufacturer', 'model', 'megapixels', 'sensors', 'type']
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))

    if (missingHeaders.length > 0) {
      return {
        valid: false,
        error: `Missing required columns: ${missingHeaders.join(', ')}`,
      }
    }

    const cameras = []
    const errors = []
    const warnings = []

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue // Skip empty lines

      const values = line.split(',').map(v => v.trim())
      const camera = {}

      headers.forEach((header, index) => {
        camera[header] = values[index] || ''
      })

      // Validate each camera
      const lineNum = i + 1

      if (!camera.manufacturer) {
        errors.push(`Line ${lineNum}: Manufacturer is required`)
      }
      if (!camera.model) {
        errors.push(`Line ${lineNum}: Model is required`)
      }
      if (!camera.megapixels || isNaN(camera.megapixels)) {
        errors.push(`Line ${lineNum}: Megapixels must be a number`)
      }
      if (!camera.sensors || isNaN(camera.sensors)) {
        errors.push(`Line ${lineNum}: Sensors must be a number`)
      }
      if (!camera.type) {
        errors.push(`Line ${lineNum}: Type is required`)
      } else if (!validTypes.includes(camera.type)) {
        warnings.push(`Line ${lineNum}: Type "${camera.type}" is unusual. Valid types: ${validTypes.join(', ')}`)
      }

      if (camera.product_link && !camera.product_link.startsWith('http')) {
        warnings.push(`Line ${lineNum}: Product link should start with http:// or https://`)
      }

      cameras.push({
        manufacturer: camera.manufacturer,
        model: camera.model,
        megapixels: parseFloat(camera.megapixels),
        sensors: parseInt(camera.sensors),
        type: camera.type,
        product_link: camera.product_link || camera.productlink || '',
      })
    }

    return {
      valid: errors.length === 0,
      cameras,
      errors,
      warnings,
      count: cameras.length,
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first')
      return
    }

    setUploading(true)
    setError(null)
    setUploadResults(null)

    try {
      // Read file
      const text = await file.text()

      // Validate
      const validation = validateCSV(text)
      setValidationResults(validation)

      if (!validation.valid) {
        setUploading(false)
        return
      }

      // Upload cameras
      const results = {
        success: [],
        failed: [],
      }

      for (const camera of validation.cameras) {
        try {
          await api.addCamera(camera)
          results.success.push(camera)
        } catch (err) {
          results.failed.push({ camera, error: err.message })
        }
      }

      setUploadResults(results)
    } catch (err) {
      setError(`Failed to process file: ${err.message}`)
    } finally {
      setUploading(false)
    }
  }

  const handleDownloadTemplate = () => {
    const link = document.createElement('a')
    link.href = '/camera-template.csv'
    link.download = 'camera-template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-8 max-w-4xl"
    >
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
          Submit Cameras
        </h1>
        <p className="text-gray-400">
          Help us expand our database by submitting camera specifications
        </p>
      </motion.div>

      {/* Instructions Card */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mb-6"
      >
        <Card
          sx={{
            backgroundColor: 'rgba(26, 26, 26, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '1rem',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          }}
        >
          <CardContent>
            <Typography variant="h6" className="text-blue-400 mb-3">
              How to Submit
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="1. Download the CSV Template"
                  secondary="Click the button below to download our template with example data"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="2. Fill in Camera Details"
                  secondary="Add camera specifications: manufacturer, model, megapixels, sensors, type, and product link"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Upload Your CSV"
                  secondary="Upload your completed file and we'll validate and import the cameras"
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2, borderColor: 'rgba(59, 130, 246, 0.2)' }} />

            <Box className="flex flex-wrap gap-2 mb-3">
              <Typography variant="body2" className="text-gray-400 w-full mb-2">
                Valid Camera Types:
              </Typography>
              {validTypes.map((type) => (
                <Chip
                  key={type}
                  label={type}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    color: '#60a5fa',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                  }}
                />
              ))}
            </Box>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                color: '#60a5fa',
                border: '1px solid rgba(59, 130, 246, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(59, 130, 246, 0.3)',
                },
              }}
            >
              Download CSV Template
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      {/* Upload Section */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <Card
          sx={{
            backgroundColor: 'rgba(26, 26, 26, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '1rem',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          }}
        >
          <CardContent>
            <Typography variant="h6" className="text-blue-400 mb-4">
              Upload Camera Data
            </Typography>

            {/* File Input */}
            <Box className="mb-4">
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="csv-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="csv-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    borderColor: 'rgba(59, 130, 246, 0.5)',
                    color: '#60a5fa',
                    '&:hover': {
                      borderColor: '#60a5fa',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    },
                  }}
                >
                  Choose CSV File
                </Button>
              </label>
              {file && (
                <Typography variant="body2" className="text-gray-400 mt-2">
                  Selected: {file.name}
                </Typography>
              )}
            </Box>

            {/* Upload Button */}
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!file || uploading}
              startIcon={uploading ? null : <CloudUploadIcon />}
              sx={{
                backgroundColor: '#3b82f6',
                '&:hover': {
                  backgroundColor: '#2563eb',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(59, 130, 246, 0.3)',
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              {uploading ? 'Processing...' : 'Validate & Upload'}
            </Button>

            {uploading && (
              <Box className="mt-4">
                <LinearProgress />
              </Box>
            )}

            {/* Error Display */}
            {error && (
              <Alert severity="error" className="mt-4" icon={<ErrorIcon />}>
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            )}

            {/* Validation Results */}
            {validationResults && (
              <Box className="mt-4">
                {validationResults.valid ? (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    <AlertTitle>Validation Passed</AlertTitle>
                    Found {validationResults.count} valid camera{validationResults.count !== 1 ? 's' : ''}
                    {validationResults.warnings.length > 0 && (
                      <Box className="mt-2">
                        <Typography variant="body2" className="font-semibold">
                          Warnings:
                        </Typography>
                        <ul className="list-disc list-inside">
                          {validationResults.warnings.map((warning, i) => (
                            <li key={i} className="text-sm">{warning}</li>
                          ))}
                        </ul>
                      </Box>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="error" icon={<ErrorIcon />}>
                    <AlertTitle>Validation Failed</AlertTitle>
                    {validationResults.error && (
                      <Typography variant="body2" className="mb-2">
                        {validationResults.error}
                      </Typography>
                    )}
                    {validationResults.errors && validationResults.errors.length > 0 && (
                      <Box>
                        <Typography variant="body2" className="font-semibold">
                          Errors found:
                        </Typography>
                        <ul className="list-disc list-inside">
                          {validationResults.errors.slice(0, 10).map((err, i) => (
                            <li key={i} className="text-sm">{err}</li>
                          ))}
                          {validationResults.errors.length > 10 && (
                            <li className="text-sm">
                              ... and {validationResults.errors.length - 10} more errors
                            </li>
                          )}
                        </ul>
                      </Box>
                    )}
                  </Alert>
                )}
              </Box>
            )}

            {/* Upload Results */}
            {uploadResults && (
              <Box className="mt-4">
                {uploadResults.success.length > 0 && (
                  <Alert severity="success" icon={<CheckCircleIcon />} className="mb-2">
                    <AlertTitle>Successfully Added</AlertTitle>
                    {uploadResults.success.length} camera{uploadResults.success.length !== 1 ? 's' : ''} added to the database!
                  </Alert>
                )}
                {uploadResults.failed.length > 0 && (
                  <Alert severity="warning" icon={<WarningIcon />}>
                    <AlertTitle>Some Cameras Failed</AlertTitle>
                    {uploadResults.failed.length} camera{uploadResults.failed.length !== 1 ? 's' : ''} could not be added:
                    <ul className="list-disc list-inside mt-2">
                      {uploadResults.failed.slice(0, 5).map((item, i) => (
                        <li key={i} className="text-sm">
                          {item.camera.manufacturer} {item.camera.model}: {item.error}
                        </li>
                      ))}
                      {uploadResults.failed.length > 5 && (
                        <li className="text-sm">
                          ... and {uploadResults.failed.length - 5} more
                        </li>
                      )}
                    </ul>
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  )
}

export default SubmitPage
